import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import OtpInput from "react-otp-input";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import toastr from "toastr";
import { useRefundRequest, useRefundVerify } from "../../api/refund";
import {
  getVerifiedRefundBankDetail,
  hasVerifiedRefundBankDetail,
  setAuthUser,
} from "../../helpers/authUserStorage";
import { formatGbp } from "../../helpers/currency";

const OTP_LENGTH = 6;
const STEP_FORM = "form";
const STEP_VERIFY = "verify";

const bankFieldsSchema = {
  accountHolderName: Yup.string().trim().required("Account holder name is required"),
  bankName: Yup.string().trim().required("Bank name is required"),
  sortCode: Yup.string().trim().required("Sort code is required"),
  accountNumber: Yup.string().trim().required("Account number is required"),
};

const RefundRequestModal = ({ isOpen, toggle, maxAmount }) => {
  const [step, setStep] = useState(STEP_FORM);
  const [refundSlug, setRefundSlug] = useState("");
  const [updateBankDetails, setUpdateBankDetails] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const hasSavedBankDetail = useMemo(
    () => hasVerifiedRefundBankDetail(),
    [isOpen]
  );
  const savedBankDetail = useMemo(() => {
    if (!hasSavedBankDetail) return null;
    return getVerifiedRefundBankDetail();
  }, [isOpen, hasSavedBankDetail]);

  const refundRequestMutation = useRefundRequest();
  const refundVerifyMutation = useRefundVerify();

  const maxCredit = useMemo(() => {
    const n = Number(maxAmount);
    return Number.isNaN(n) ? 0 : n;
  }, [maxAmount]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        amount: Yup.number()
          .typeError("Enter a valid amount")
          .positive("Amount must be greater than zero")
          .max(
            maxCredit > 0 ? maxCredit : 0,
            maxCredit > 0
              ? `Amount cannot exceed available credit (${formatGbp(maxCredit)})`
              : "No credit available to refund"
          )
          .required("Amount is required"),
        ...(hasSavedBankDetail && !updateBankDetails ? {} : bankFieldsSchema),
      }),
    [hasSavedBankDetail, updateBankDetails, maxCredit]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      amount: "",
      accountHolderName: savedBankDetail?.accountHolderName ?? "",
      bankName: savedBankDetail?.bankName ?? "",
      sortCode: savedBankDetail?.sortCode ?? "",
      accountNumber: savedBankDetail?.accountNumber ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      const amount = Number(values.amount);
      const includeBankDetails = !hasSavedBankDetail || updateBankDetails;

      const payload = includeBankDetails
        ? {
            accountHolderName: values.accountHolderName.trim(),
            bankName: values.bankName.trim(),
            sortCode: values.sortCode.trim(),
            accountNumber: values.accountNumber.trim(),
            amount,
          }
        : { amount };

      refundRequestMutation.mutate(payload, {
        onSuccess: (res) => {
          const slug = res?.data?.slug;
          if (!slug) {
            toastr.error("Refund request failed. Please try again.");
            return;
          }
          setRefundSlug(slug);
          setStep(STEP_VERIFY);
          setOtpCode("");
          toastr.success(
            res?.message ||
              "Refund request created. A verification code has been sent to your email."
          );
        },
      });
    },
  });

  const resetModal = () => {
    setStep(STEP_FORM);
    setRefundSlug("");
    setUpdateBankDetails(false);
    setOtpCode("");
    formik.resetForm();
    refundRequestMutation.reset();
    refundVerifyMutation.reset();
  };

  const handleToggle = () => {
    resetModal();
    toggle();
  };

  useEffect(() => {
    if (!isOpen) return;
    const detail = getVerifiedRefundBankDetail();
    setUpdateBankDetails(!hasVerifiedRefundBankDetail());
    setStep(STEP_FORM);
    setRefundSlug("");
    setOtpCode("");
    formik.resetForm({
      values: {
        amount: "",
        accountHolderName: detail?.accountHolderName ?? "",
        bankName: detail?.bankName ?? "",
        sortCode: detail?.sortCode ?? "",
        accountNumber: detail?.accountNumber ?? "",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const showBankFields = !hasSavedBankDetail || updateBankDetails;
  const isSubmitting =
    refundRequestMutation.isPending || refundVerifyMutation.isPending;

  const handleVerify = () => {
    const code = otpCode.replace(/\D/g, "");
    if (code.length !== OTP_LENGTH) {
      toastr.error("Please enter the 6-digit verification code.");
      return;
    }
    refundVerifyMutation.mutate(
      { slug: refundSlug, code },
      {
        onSuccess: (res) => {
          const user = res?.data;
          if (user) {
            setAuthUser(user);
          }
          toastr.success(
            res?.message || "Refund request verified successfully."
          );
          handleToggle();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="lg">
      <ModalHeader toggle={handleToggle}>
        {step === STEP_VERIFY ? "Verify refund request" : "Request refund"}
      </ModalHeader>
      {step === STEP_FORM ? (
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody>
            {maxCredit <= 0 && (
              <Alert color="warning" className="mb-3">
                You have no available credit to request a refund.
              </Alert>
            )}

            <FormGroup>
              <Label for="refund-amount">Refund amount (£)</Label>
              <Input
                id="refund-amount"
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                max={maxCredit > 0 ? maxCredit : undefined}
                placeholder="0.00"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.amount && Boolean(formik.errors.amount)}
                disabled={maxCredit <= 0}
              />
              {formik.touched.amount && formik.errors.amount ? (
                <FormFeedback>{formik.errors.amount}</FormFeedback>
              ) : (
                <small className="text-muted">
                  Available credit: {formatGbp(maxCredit)}
                </small>
              )}
            </FormGroup>

            {hasSavedBankDetail && (
              <div className="border rounded p-3 mb-3 bg-light">
                <p className="mb-2 fw-semibold">Saved bank details</p>
                <div className="small text-muted mb-2">
                  <div>
                    <strong>Account holder:</strong>{" "}
                    {savedBankDetail.accountHolderName}
                  </div>
                  <div>
                    <strong>Bank:</strong> {savedBankDetail.bankName}
                  </div>
                  <div>
                    <strong>Sort code:</strong> {savedBankDetail.sortCode}
                  </div>
                  <div>
                    <strong>Account number:</strong>{" "}
                    {savedBankDetail.accountNumber}
                  </div>
                </div>
                <FormGroup check className="mb-0">
                  <Input
                    type="checkbox"
                    id="update-bank-details"
                    checked={updateBankDetails}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUpdateBankDetails(checked);
                      if (checked) {
                        const sc = formik.values.sortCode ?? "";
                        const an = formik.values.accountNumber ?? "";
                        formik.setValues({
                          ...formik.values,
                          sortCode: sc.includes("*") ? "" : sc,
                          accountNumber: an.includes("*") ? "" : an,
                        });
                      }
                    }}
                  />
                  <Label check for="update-bank-details" className="ms-1">
                    Update bank details for this refund
                  </Label>
                </FormGroup>
              </div>
            )}

            {showBankFields && (
              <>
                <FormGroup>
                  <Label for="accountHolderName">Account holder name</Label>
                  <Input
                    id="accountHolderName"
                    name="accountHolderName"
                    value={formik.values.accountHolderName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.accountHolderName &&
                      Boolean(formik.errors.accountHolderName)
                    }
                  />
                  {formik.touched.accountHolderName &&
                    formik.errors.accountHolderName && (
                      <FormFeedback>
                        {formik.errors.accountHolderName}
                      </FormFeedback>
                    )}
                </FormGroup>
                <FormGroup>
                  <Label for="bankName">Bank name</Label>
                  <Input
                    id="bankName"
                    name="bankName"
                    value={formik.values.bankName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.bankName && Boolean(formik.errors.bankName)
                    }
                  />
                  {formik.touched.bankName && formik.errors.bankName && (
                    <FormFeedback>{formik.errors.bankName}</FormFeedback>
                  )}
                </FormGroup>
                <RowFields formik={formik} />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={handleToggle}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isSubmitting || maxCredit <= 0}
            >
              {refundRequestMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting…
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </ModalFooter>
        </Form>
      ) : (
        <>
          <ModalBody>
            <p className="text-muted mb-3">
              Enter the 6-digit verification code sent to your email to confirm
              this refund request.
            </p>
            <div className="d-flex justify-content-center mb-2">
              <OtpInput
                value={otpCode}
                onChange={(val) =>
                  setOtpCode((val || "").replace(/\D/g, "").slice(0, OTP_LENGTH))
                }
                numInputs={OTP_LENGTH}
                shouldAutoFocus
                inputType="tel"
                renderInput={(props) => <input {...props} />}
                inputStyle={{
                  width: "40px",
                  height: "48px",
                  margin: "0 6px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "20px",
                  textAlign: "center",
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="link"
              type="button"
              className="text-muted"
              onClick={() => {
                setStep(STEP_FORM);
                setOtpCode("");
              }}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              color="primary"
              type="button"
              onClick={handleVerify}
              disabled={isSubmitting || otpCode.length !== OTP_LENGTH}
            >
              {refundVerifyMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Verifying…
                </>
              ) : (
                "Verify & submit"
              )}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

function RowFields({ formik }) {
  return (
    <div className="row">
      <div className="col-md-6">
        <FormGroup>
          <Label for="sortCode">Sort code</Label>
          <Input
            id="sortCode"
            name="sortCode"
            value={formik.values.sortCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.sortCode && Boolean(formik.errors.sortCode)
            }
          />
          {formik.touched.sortCode && formik.errors.sortCode && (
            <FormFeedback>{formik.errors.sortCode}</FormFeedback>
          )}
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="accountNumber">Account number</Label>
          <Input
            id="accountNumber"
            name="accountNumber"
            value={formik.values.accountNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.accountNumber &&
              Boolean(formik.errors.accountNumber)
            }
          />
          {formik.touched.accountNumber && formik.errors.accountNumber && (
            <FormFeedback>{formik.errors.accountNumber}</FormFeedback>
          )}
        </FormGroup>
      </div>
    </div>
  );
}

export default RefundRequestModal;
